import { patchConversation } from 'api/v2/AI';
import cn from 'classnames';
import { Button } from 'components/atoms/Button';
import { Checkbox } from 'components/atoms/Form/Checkbox';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import { Modal } from 'components/atoms/Modal';
import type { ModalApi } from 'components/atoms/Modal/Modal.types';
import type { FC } from 'react';
import { useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import type { ConversationRating } from 'types/ai.types';
import { World } from 'types/shared/worlds';
import { StarIcon } from 'utils/icons';
import { useAIChatContext } from './AIChat.context';

type AIChatRatingProps = {
  isSmall?: boolean;
};

const AIChatRating: FC<AIChatRatingProps> = ({ isSmall }) => {
  const { t } = useTranslation();
  const { conversationId, conversation, refetchConversation, isStreaming } =
    useAIChatContext();

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [ratingValue, setRatingValue] = useState(
    conversation?.rating?.rate || 0,
  );
  const [hoveredRating, setHoveredRating] = useState(0);

  const methods = useForm({
    defaultValues: {
      comment: '',
      untruthful: false,
      useless: false,
    },
  });

  const apiModalRef = useRef<ModalApi>(null);
  const isPositiveRate = ratingValue > 2;

  const onSubmitSuccess = () => {
    refetchConversation?.();
    setIsSubmitSuccessful(true);
    methods.reset();
    apiModalRef.current?.closeModal();
  };

  const { mutateAsync: rateConversation, isLoading } = useMutation(
    (data: Omit<ConversationRating, 'rate'> = {}) => {
      const { comment, untruthful, useless } = data;

      const rating = {
        rate: ratingValue,
        comment: comment?.length ? comment : null,
        untruthful: isPositiveRate ? null : untruthful,
        useless: isPositiveRate ? null : useless,
      };
      return patchConversation(conversationId, { rating });
    },
    { onSuccess: onSubmitSuccess },
  );

  const onSubmitFeedback = (data: Omit<ConversationRating, 'rate'>) =>
    rateConversation(data);

  const onModalClose = ({ closeModal }: Pick<ModalApi, 'closeModal'>) => {
    if (isLoading) return;
    if (!isSubmitSuccessful) {
      methods.handleSubmit(onSubmitFeedback)();
    }
    closeModal();
  };

  const onRatingChange = (rating: number) => {
    setRatingValue(rating);
    setIsSubmitSuccessful(false);
    apiModalRef.current?.openModal();
  };

  const lastMessagePartIsText = useMemo(() => {
    const messages = conversation?.messages ?? [];
    const lastMessage = messages[messages.length - 1];
    const lastMessagePart = lastMessage?.parts?.[lastMessage?.parts.length - 1];
    return lastMessagePart?.type === 'text';
  }, [conversation]);

  if (
    isStreaming ||
    conversation?.status !== 'processed' ||
    !lastMessagePartIsText
  )
    return null;

  return (
    <FormProvider {...methods}>
      <Modal
        name="Feedback Modal"
        onBlur={onModalClose}
        apiRef={apiModalRef}
        trigger={() => (
          <div className="mt-2">
            <p className="text-lg font-bold text-center my-2">
              {t('ai_bot.feedback.title')}
            </p>
            <div className="flex items-center justify-center space-x-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <div
                  key={star}
                  className="cursor-pointer rounded-lg border border-primary-100 p-2"
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                  onClick={() => onRatingChange(star)}
                >
                  <StarIcon
                    className={cn({
                      'w-8 h-8': !isSmall,
                      'w-4 h-4': isSmall,
                      'text-taxes': hoveredRating
                        ? hoveredRating >= star
                        : ratingValue >= star,
                      'text-primary-400': hoveredRating
                        ? hoveredRating < star
                        : ratingValue < star,
                    })}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        render={() => (
          <div className="flex flex-col px-6 py-4 items-center">
            <span className="text-2xl text-primary-700 font-bold mb-4">
              {t('ai_bot.feedback.modal.title')}
            </span>
            <div className="flex flex-col space-y-2 w-full">
              <FieldConnector name="comment" className="w-full">
                {({ field }) => (
                  <FreeTextInput
                    {...field}
                    className="!h-24"
                    type="textarea"
                    placeholder={
                      ratingValue > 2
                        ? t('ai_bot.feedback.modal.what_is_good')
                        : t('ai_bot.feedback.modal.what_is_bad')
                    }
                  />
                )}
              </FieldConnector>
              {!isPositiveRate && (
                <FieldConnector
                  name="useless"
                  className="self-start text-primary-700 font-semibold text-sm"
                >
                  {({ field }) => (
                    <div>
                      <Checkbox
                        {...field}
                        id="useless"
                        title={t('ai_bot.feedback.modal.options.useless')}
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                      <label htmlFor="useless" className="pl-2">
                        {t('ai_bot.feedback.modal.not_useful')}
                      </label>
                    </div>
                  )}
                </FieldConnector>
              )}
              {!isPositiveRate && (
                <FieldConnector
                  name="untruthful"
                  className="self-start text-primary-700 font-semibold text-sm"
                >
                  {({ field }) => (
                    <div>
                      <Checkbox
                        {...field}
                        id="untruthful"
                        title={t('ai_bot.feedback.modal.options.untruthful')}
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                      <label htmlFor="untruthful" className="pl-2">
                        {t('ai_bot.feedback.modal.not_true')}
                      </label>
                    </div>
                  )}
                </FieldConnector>
              )}
              <Button
                onClick={methods.handleSubmit(onSubmitFeedback)}
                className="w-auto !mt-4 m-auto px-6"
                color={World.TAXES}
                disabled={isLoading}
              >
                {isLoading
                  ? t('loading')
                  : t('ai_bot.feedback.modal.submit_feedback')}
              </Button>
            </div>
          </div>
        )}
      />
    </FormProvider>
  );
};

export default AIChatRating;
